// --------------------------------------------------------------
// Created On: 2023-01-26
// Author: Zachary Thomas
//
// Last Modified: 2026-02-10
// Modified By: Hannah Vaughan
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useReducer } from "react";
import { API } from "../../constants/miscellaneous";
import { REPORT_TYPES } from "../../constants/reducerActions";
import useReduxSelector from "../../hooks/useReduxSelector";
import { CREATE_REPORTS_PERMISSION } from "../../constants/permissions";
import deepCopy from "../../utilities/deepCopy";
import useApi from "../../hooks/useApi";
import Spinner from "../../components/Spinner/Spinner";
import Error500Page from "../Error500Page/Error500Page";
import ResourceList from "../../components/ResourceList/ResourceList";
import ReportModal from "./ReportModal/ReportModal";
import MemphisReportModal from "./MemphisReportModal/MemphisReportModal";
import ReportListRow from "./ReportListRow/ReportListRow";
import MemphisReportListRow from "../ManageReportsPage/MemphisReportListRow/MemphisReportListRow";
import HelpReport from "../../components/HelpReport/HelpReport";
import HelpMemphisReport from "src/components/HelpMemphisReport/HelpMemphisReport";
import styles from "./ManageReportsPage.module.scss";

// Page for creating, editing, and deleting reports.
export default function ManageReportsPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [selectedMemphisReportId, setSelectedMemphisReportId] = useState<number>(-1);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [assetgroups, setAssetgroups] = useState<ReportAssetgroup[]>([]);
  const [memphisReportIsAllowed, setMemphisReportIsAllowed] = useState<boolean>(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [reports, dispatch] = useReducer(reportReducer, []);
  const [memphisReports, memphisReportsDispatch] = useReducer(memphisReportReducer, []);
  const [reportTypes, setReportTypes] = useState<ReportType[]>([]);
  const [comparators, setComparators] = useState<Comparator[]>([]);
  const [enumerationMap, setEnumerationMap] = useState<EnumerationMap>({});
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Get a list of reports from the API.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/report`,
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        dispatch({
          type: REPORT_TYPES.SET_REPORTS,
          payload: {
            reports: responseBody.reports,
          },
        });
        memphisReportsDispatch({
          type: REPORT_TYPES.SET_REPORTS,
          payload: {
            memphisReports: responseBody.memphisReports,
          },
        });
        setAssetgroups(responseBody.assetgroups);
        setAttributes(responseBody.attributes);
        setMemphisReportIsAllowed(responseBody.memphisReportIsAllowed);
        setAssets(responseBody.assets);
        setReportTypes(responseBody.reportTypes);
        setComparators(responseBody.comparators);
        setEnumerationMap(responseBody.enumerationMap);

        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
      }
      setLoading(false);
    },
    []
  );

  // Report reducer.
  function reportReducer(state: AssetReport[], action: Action): AssetReport[] {
    switch (action.type) {
      case REPORT_TYPES.SET_REPORTS: {
        if (action.payload.reports !== undefined) {
          return sortReports(action.payload.reports);
        } else {
          return [];
        }
      }

      case REPORT_TYPES.CREATE_REPORT: {
        let stateDeepCopy = deepCopy(state);
        const newReport = action.payload.report;
        if (newReport !== undefined) {
          // Check if the report already exists before creating them.
          const reportExists = stateDeepCopy.some((report) => report.reportId === newReport.reportId);
          if (!reportExists) {
            stateDeepCopy.push(newReport);
            stateDeepCopy = sortReports(stateDeepCopy);
          }
        }
        return stateDeepCopy;
      }

      case REPORT_TYPES.UPDATE_REPORT: {
        let stateDeepCopy = deepCopy(state);
        const updatedReport = action.payload.report;
        if (updatedReport !== undefined) {
          // Find the index of the report.
          const reportIndex = stateDeepCopy.findIndex((report) => report.reportId === updatedReport.reportId);

          // Don't continue if we couldn't find the report.
          if (reportIndex === -1) {
            return stateDeepCopy;
          }

          // Replace the current report with the updated one.
          stateDeepCopy.splice(reportIndex, 1, updatedReport);

          // Sort the reports.
          stateDeepCopy = sortReports(stateDeepCopy);
        }
        return stateDeepCopy;
      }

      case REPORT_TYPES.DELETE_REPORT: {
        const stateDeepCopy = deepCopy(state);
        const reportId = action.payload.reportId;

        if (reportId !== undefined) {
          // Find the index of the report.
          const reportIndex = stateDeepCopy.findIndex((report) => report.reportId === reportId);

          // Don't continue if we couldn't find the report.
          if (reportIndex === -1) {
            return stateDeepCopy;
          }

          // Delete the current report.
          stateDeepCopy.splice(reportIndex, 1);
        }
        return stateDeepCopy;
      }

      default: {
        return state;
      }
    }
  }

  // Memphis Report reducer.
  function memphisReportReducer(state: MemphisReport[], action: Action): MemphisReport[] {
    switch (action.type) {
      case REPORT_TYPES.SET_REPORTS: {
        if (action.payload.memphisReports !== undefined) {
          return sortMemphisReports(action.payload.memphisReports);
        } else {
          return [];
        }
      }

      case REPORT_TYPES.CREATE_REPORT: {
        let stateDeepCopy = deepCopy(state);
        const newReport = action.payload.memphisReport;
        if (newReport !== undefined) {
          // Check if the report already exists before creating them.
          const reportExists = stateDeepCopy.some((report) => report.reportMemphisId === newReport.reportMemphisId);
          if (!reportExists) {
            stateDeepCopy.push(newReport);
            stateDeepCopy = sortMemphisReports(stateDeepCopy);
          }
        }
        return stateDeepCopy;
      }

      case REPORT_TYPES.UPDATE_REPORT: {
        let stateDeepCopy = deepCopy(state);
        const updatedReport = action.payload.memphisReport;
        if (updatedReport !== undefined) {
          // Find the index of the report.
          const reportIndex = stateDeepCopy.findIndex(
            (report) => report.reportMemphisId === updatedReport.reportMemphisId
          );

          // Don't continue if we couldn't find the report.
          if (reportIndex === -1) {
            return stateDeepCopy;
          }

          // Replace the current report with the updated one.
          stateDeepCopy.splice(reportIndex, 1, updatedReport);

          // Sort the reports.
          stateDeepCopy = sortMemphisReports(stateDeepCopy);
        }
        return stateDeepCopy;
      }

      case REPORT_TYPES.DELETE_REPORT: {
        const stateDeepCopy = deepCopy(state);
        const reportId = action.payload.reportMemphisId;

        if (reportId !== undefined) {
          // Find the index of the report.
          const reportIndex = stateDeepCopy.findIndex((report) => report.reportMemphisId === reportId);

          // Don't continue if we couldn't find the report.
          if (reportIndex === -1) {
            return stateDeepCopy;
          }

          // Delete the current report.
          stateDeepCopy.splice(reportIndex, 1);
        }
        return stateDeepCopy;
      }

      default: {
        return state;
      }
    }
  }

  // Sort reports.
  function sortReports(reports: AssetReport[]): AssetReport[] {
    return reports.sort((a, b) => {
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();
      if (nameA < nameB) {
        return -1;
      } else if (nameA > nameB) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  // Sort Memphis reports.
  function sortMemphisReports(reports: MemphisReport[]): MemphisReport[] {
    return reports.sort((a, b) => {
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();
      if (nameA < nameB) {
        return -1;
      } else if (nameA > nameB) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  return failedToLoad ? (
    <Error500Page />
  ) : (
    <div className="p-4">
      <Spinner loading={loading} />

      <div className="row">
        {/* Report management. */}
        <div className={`${styles.list} col-12 col-md mx-0`}>
          <ResourceList
            resourceNameSingular="Report"
            resourceNamePlural="Reports"
            resourceArticle="a"
            headerButtonLabel="Create Report"
            headerButtonLabelSmall="Create"
            headerButtonUserPermissions={[[CREATE_REPORTS_PERMISSION]]}
            resourceIdKey="reportId"
            resources={reports}
            resourceRow={ReportListRow}
            helpModal={<HelpReport />}
            filterKeys={["name"]}
            loading={loading}
            onClickHeaderButton={() => setSelectedId(0)}
            onSelect={(selectedId) => setSelectedId(selectedId)}
          />
        </div>

        {/* Show Memphis Reports list if 'memphisReportIsAllowed' === true */}
        {memphisReportIsAllowed && (
          <div className={`${styles.list} col-12 col-md mx-0`}>
            <ResourceList
              resourceNameSingular="City of Memphis Report"
              resourceNamePlural="City of Memphis Reports"
              resourceArticle="a"
              headerButtonLabel="Create Report"
              headerButtonLabelSmall="Create"
              headerButtonUserPermissions={[[CREATE_REPORTS_PERMISSION]]}
              resourceIdKey="reportMemphisId"
              resources={memphisReports}
              resourceRow={MemphisReportListRow}
              helpModal={<HelpMemphisReport />}
              filterKeys={["name"]}
              loading={loading}
              onClickHeaderButton={() => setSelectedMemphisReportId(0)}
              onSelect={(selectedMemphisReportId) => setSelectedMemphisReportId(selectedMemphisReportId)}
            />
          </div>
        )}

        {/* Report modal for editing reports. */}
        {selectedId >= 0 && (
          <ReportModal
            isCreatingNewRecord={selectedId === 0}
            reportId={selectedId}
            assetgroups={assetgroups}
            attributes={attributes}
            reportTypes={reportTypes}
            comparators={comparators}
            enumerationMap={enumerationMap}
            onClose={() => setSelectedId(-1)}
            onAction={(action) => dispatch(action)}
          />
        )}

        {/* Report modal for editing Memphis reports. */}
        {selectedMemphisReportId >= 0 && (
          <MemphisReportModal
            isCreatingNewRecord={selectedMemphisReportId === 0}
            reportMemphisId={selectedMemphisReportId}
            assets={assets}
            memphisReportIsAllowed={memphisReportIsAllowed}
            onClose={() => setSelectedMemphisReportId(-1)}
            onAction={(action) => memphisReportsDispatch(action)}
          />
        )}

        <datalist id="datalist-attributes">
          {attributes.map((attribute) => (
            <option key={attribute.attributeId} value={attribute.name} />
          ))}
        </datalist>

        <datalist id="datalist-assetgroups">
          {assetgroups.map((assetgroup) => (
            <option key={assetgroup.assetgroupId} value={assetgroup.name} />
          ))}
        </datalist>

        <datalist id="datalist-assets">
          {assets.map((asset) => (
            <option key={asset.assetId} value={asset.name} />
          ))}
        </datalist>
      </div>
    </div>
  );
}

interface ResponseBody {
  reports: AssetReport[];
  assetgroups: ReportAssetgroup[];
  assets: Asset[];
  attributes: AttributeWithCode[];
  memphisReportIsAllowed: boolean;
  memphisReports: MemphisReport[];
  reportTypes: ReportType[];
  comparators: Comparator[];
  enumerationMap: EnumerationMap;
}

interface AttributeWithCode {
  attributeId: number;
  code: string;
  name: string;
  isBoolean: boolean;
  unitShortName?: string;
  regEnumId?: number;
}

interface AssetReport {
  reportId: number;
  name: string;
  enabled: boolean;
  type: string;
  hours: number;
  attributes: Attribute[];
  emailAddresses: string[];
  scheduleSettings: ReportSchedule[];
  assetgroups: ReportAssetgroup[];
  isAssetRunning: boolean;
}

interface MemphisReport {
  reportMemphisId: number | null;
  name: string;
}

interface ReportSchedule {
  reportScheduleId: number;
  type: string;
  value: string;
}

interface ReportAssetgroup {
  assetgroupId: number;
  name: string;
}

interface Attribute {
  attributeId: number;
  name: string;
  isBoolean: boolean;
  unitShortName?: string;
  regEnumId?: number;
}

interface Asset {
  assetId: number;
  name: string;
}

interface ReportType {
  reportTypeId: number;
  code: string;
  description: string;
  userHasAccess: boolean;
  allowsAttributes: boolean;
  allowsComparators: boolean;
  allowsAssetRunning: boolean;
}

interface Comparator {
  alertThresholdComparatorId: number;
  name: string;
}

interface EnumerationMap {
  [key: string]: EnumerationIndexMap;
}

interface EnumerationIndexMap {
  [key: string]: string;
}

interface Action {
  type: string;
  payload: Payload;
}

interface Payload {
  reports?: AssetReport[];
  memphisReports?: MemphisReport[];
  report?: AssetReport;
  memphisReport?: MemphisReport;
  reportId?: number;
  reportMemphisId?: number;
}
